import { PdfProTip } from '@/components/ModelTip/ModelTip'
import { UpgradeLinkBtn } from '@/components/UpgradeLinkBtn/UpgradeLinkBtn'
import { VideoItem } from '@/components/VideoItem/VideoItem'
import { toastController } from '@/controllers/toastController'
import { commonUtils } from '@/helpers/commonUtils'
import { envHelper } from '@/helpers/envHelper'
import { userService } from '@/services/userService'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Popover } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import style from './Guide.module.less'

/** Guide 组件的 Props */
export type GuideProps = {
  onLoadOnlinePdf: (url: string) => void
  onChooseFile: () => void
  onOpenHistoryModal: () => void
}

export const Guide = observer((props: GuideProps) => {
  const { onChooseFile, onLoadOnlinePdf, onOpenHistoryModal } = props
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {}, [])

  function handleLoadOnlinePdf() {
    if (!commonUtils.isPdfUrl(inputValue)) {
      toastController.warning('请输入有效的在线 pdf 链接，以 .pdf 结尾')
      return
    }
    onLoadOnlinePdf(inputValue)
    setInputValue('')
  }

  return (
    <div className={classNames(style.guide)}>
      <div className={style.title}>
        <div
          className={classNames(style.pdf, {
            [style.oemLenovo]: envHelper.oem === 'lenovo',
          })}
        >
          「分段对照翻译+AI解析」
        </div>
        <div className={style.dot}>·</div>
        <div className={style.text}>表格公式完美解析</div>

        <img className={style.curve} src={commonUtils.getStaticFile('pdf/curve-v1.png')}></img>
      </div>

      <div className={classNames(style.main)}>
        {userService.isLogin && (
          <div className={classNames(style.readHistoryEntry)} onClick={onOpenHistoryModal}>
            <img
              className={style.historyIcon}
              src={commonUtils.getStaticFile('pdf/tool-history.png')}
            />
            <span className={style.text}>历史记录</span>
          </div>
        )}
        <div className={classNames(style.introBox)}>
          <div className={style.itemWrap}>
            <img className={style.tipIcon} src={commonUtils.getStaticFile('pdf/tip1.png')}></img>

            <div className={classNames(style.textWrap)}>
              <div className={style.text}>
                分段翻译，阅读效率更高
                <Popover
                  content={
                    <VideoItem
                      src='https://cdn.yiban.io/web/pt-web/pdf-pro-guide.mp4'
                      autoPlay
                      className={classNames(style.video)}
                    ></VideoItem>
                  }
                  placement={'right'}
                  getPopupContainer={(el) => el.parentElement!}
                >
                  <QuestionCircleOutlined className={style.questionIcon} />
                </Popover>
              </div>
              <div className={classNames(style.text2)}>
                逐段上下对照的排版，自上而下的阅读顺序，易读性更强
              </div>
            </div>
          </div>

          <div className={style.itemWrap}>
            <img className={style.tipIcon} src={commonUtils.getStaticFile('pdf/tip2-v1.png')}></img>

            <div className={classNames(style.textWrap)}>
              <div className={style.text}>AI驱动，翻译更准</div>
              <div className={classNames(style.text2)}>
                AI驱动，无论是复杂的图表、公式、或是专业的科研术语都能准确翻译
              </div>
            </div>
          </div>

          <div className={style.itemWrap}>
            <img className={style.tipIcon} src={commonUtils.getStaticFile('pdf/tip3.png')}></img>

            <div className={classNames(style.textWrap)}>
              <div className={style.text}>高效准确，提升阅读效率</div>
              <div className={classNames(style.text2)}>
                AI快速、准确地将文件中的文本翻译成目标语言，为您的工作/学习提供高效支持
              </div>
            </div>
          </div>
        </div>
        <div className={classNames(style.rightBox)}>
          {userService.isLogin && (
            <div className={classNames(style.dataArea)}>
              <div className={classNames(style.dataItem)}>
                <p className={classNames(style.text)}>当前版本：{userService.vipVersionName}</p>
                {userService.vipVersion === 'free' && (
                  <UpgradeLinkBtn
                    version='max'
                    feature='pdfPro'
                    source='pdf_pro_guide'
                    text='立即升级，畅享分段翻译'
                    styleCSS={{ fontSize: '14px', marginLeft: 12 }}
                  />
                )}
              </div>

              <div className={classNames(style.dataItem)}>
                <p className={classNames(style.text)}>
                  剩余 PDF 解析页数{' '}
                  <PdfProTip
                    showToken={true}
                    styleCSS={{ width: '14px', height: '14px' }}
                    dailyFreeCount={userService.remainGiftPdfProPageCount}
                    dailyFreeCountTotal={userService.totalGiftPdfProPageCount}
                    vipCount={userService.remainVipPdfProPageCount}
                    vipTotalCount={userService.totalVipPdfProPageCount}
                    packageCount={userService.remainBuyPdfProPageCount}
                    packageCountTotal={userService.totalBuyPdfProPageCount}
                  />
                  ：{userService.remainPdfProPageCount.toLocaleString()} 页
                </p>
                <p className={classNames(style.text)}>
                  {userService.vipVersion !== 'free' && (
                    <span
                      className={classNames(style.upgrade)}
                      onClick={() => {
                        userService.doPay({
                          from: 'pdf_pro_guide_buy_package',
                          wordPack: true,
                          wordPackType: 'pdf_pro',
                          feature: 'pdfPro',
                        })
                      }}
                    >
                      获取更多页数
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          <div className={classNames(style.uploadArea)} onClick={onChooseFile}>
            <img src={commonUtils.getStaticFile('pdf/pdf-upload.png')} />
            <p className={classNames(style.text1)}>点击选择或拖拽文件至此上传</p>
            <p className={classNames(style.text2)}>
              由 AI 驱动的 PDF 分段翻译，提供上下对照的翻译效果
            </p>
            <p className={classNames(style.text2)}>
              适用于科研论文、技术文档等专业性较强文档的查阅场景
            </p>
            <p className={classNames(style.text2)}>
              注：<strong>分段翻译不会保留 PDF 原排版</strong>，若您希望保留排版，请使用「PDF
              无损翻译」
            </p>
          </div>
        </div>
      </div>
    </div>
  )
})
